import { FC, FormEvent, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthActions } from "@/components/AuthProvider";
import { updateRegistrationInfo } from "@/server/auth";
import { toastManager } from "@/components/ui/toast";
import { MaskedDateInput } from "@/components/ui/maskedDateInput";
import { isMinimumAge } from "@/utils/ageUtils";
import { parseDateInput } from "@/utils/dateInputUtils";
import type { Profile } from "@/apiTypes/profile";

interface RegistrationInfoModalProps {
  profile: Profile;
  /** When true, the liability waiver field is not required. */
  skipRiderFields?: boolean;
  onComplete: () => void;
  onClose: () => void;
}

/** Checks which registration fields are still missing from the profile. */
function getMissingFields(profile: Profile) {
  return {
    name: !profile.firstName || !profile.lastName,
    dateOfBirth: !profile.dateOfBirth,
    waiver: !profile.liabilityWaiverAcceptedAt,
  };
}

/**
 * Modal that collects only the missing registration info (name, DOB,
 * liability waiver) before a user's first event registration.
 */
export const RegistrationInfoModal: FC<RegistrationInfoModalProps> = ({
  profile,
  skipRiderFields = false,
  onComplete,
  onClose,
}) => {
  const { invalidate } = useAuthActions();
  const allMissing = getMissingFields(profile);
  const missing = skipRiderFields
    ? { ...allMissing, waiver: false }
    : allMissing;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [dobError, setDobError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const formData = new FormData(e.currentTarget);

      const firstName = missing.name
        ? (formData.get("firstName") as string).trim()
        : profile.firstName!;
      const lastName = missing.name
        ? (formData.get("lastName") as string).trim()
        : profile.lastName!;
      const dateOfBirth = missing.dateOfBirth
        ? parseDateInput((formData.get("dateOfBirth") as string).trim())
        : new Date(profile.dateOfBirth!);

      if (!firstName || !lastName) {
        toastManager.add({
          title: "Please fill in all fields",
          type: "error",
        });
        return;
      }

      if (!dateOfBirth) {
        setDobError("Please enter a valid date (MM/DD/YYYY).");
        return;
      }

      if (!isMinimumAge(dateOfBirth)) {
        setDobError(
          "You must be at least 18 years old to register for events.",
        );
        return;
      }
      setDobError(null);

      if (missing.waiver && !waiverAccepted) {
        toastManager.add({
          title: "Please accept the liability waiver",
          type: "error",
        });
        return;
      }

      setIsSubmitting(true);
      try {
        await updateRegistrationInfo({
          data: {
            firstName,
            lastName,
            dateOfBirth,
            ...(!skipRiderFields && { liabilityWaiverAccepted: true }),
          },
        });
        await invalidate();
        onComplete();
      } catch (err) {
        toastManager.add({
          title: err instanceof Error ? err.message : "Failed to save info",
          type: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [waiverAccepted, invalidate, onComplete, missing, profile],
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        role="presentation"
      />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold font-sans text-gray-900">
            Complete Your Profile
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            We need a few details before you can register for events.
          </p>

          {missing.name && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName" className="mb-1.5">
                  First Name
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  required
                  placeholder="First name"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="mb-1.5">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  required
                  placeholder="Last name"
                />
              </div>
            </div>
          )}

          {missing.dateOfBirth && (
            <div>
              <Label htmlFor="dateOfBirth" className="mb-1.5">
                Date of Birth
              </Label>
              <MaskedDateInput id="dateOfBirth" name="dateOfBirth" required />
              {dobError && (
                <p className="text-xs text-red-600 mt-1.5">{dobError}</p>
              )}
            </div>
          )}

          {missing.waiver && (
            <div className="border-t border-gray-200 pt-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={waiverAccepted}
                  onChange={(e) => setWaiverAccepted(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">
                  I acknowledge that cycling activities involve inherent risks,
                  and I accept full responsibility for my participation. I waive
                  any liability claims against event organizers for injuries
                  sustained during events.
                </span>
              </label>
            </div>
          )}

          <Button
            type="submit"
            disabled={isSubmitting || (missing.waiver && !waiverAccepted)}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white h-11 text-sm font-medium"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isSubmitting ? "Saving..." : "Continue to Registration"}
          </Button>
        </form>
      </div>
    </div>,
    document.body,
  );
};
